import React, { useState } from 'react';
import { StatusCodeDot } from '@hum/design-system';
import {
  User,
  CompanyContactStatus,
  CompanyContact,
  UserIdentity,
} from '@hum/types';
import { Pill, Table, TableCellConfig, TableMenuItem } from '@hum/legacy-ui';
import { Spinner, InfoSection, Empty } from '@hum/ui-library';
import { AddUser } from './AddUser';
import {
  getCompanyUsers,
  resendInvite,
  useApiMutation,
  useApiQuery,
  deleteCompanyUser,
  updateCompanyUserStatus,
  getCompany,
} from '@hum/api';

const TABLE_CELLS: Record<string, TableCellConfig> = {
  name: {
    label: 'name',
    renderer: ({ item: user }: { item: CompanyContact }) => {
      return (
        <>
          {`${user.firstName} ${user.lastName} `}
          {user.isPrimaryContact && <Pill>Primary</Pill>}
        </>
      );
    },
  },
  email: {
    label: 'email',
    renderer: ({ item: user }: { item: CompanyContact }) => <>{user.email}</>,
  },
  acceptedCompanyCommunicationsOptIn: {
    label: 'Accepted communications?',
    renderer: ({ item: user }: { item: CompanyContact }) => (
      <>
        {user.acceptedCompanyCommunicationsOptIn === undefined ||
        user.acceptedCompanyCommunicationsOptIn === null
          ? 'N/A'
          : user.acceptedCompanyCommunicationsOptIn === true
          ? 'Yes'
          : 'No'}
      </>
    ),
  },
  status: {
    label: 'status',
    renderer: ({ item }: { item: CompanyContact }) => (
      <>
        <StatusCodeDot
          active={item.status === CompanyContactStatus.Active}
          disabled={item.status === CompanyContactStatus.Disabled}
          warning={item.status === CompanyContactStatus.Invited}
        />
        {item.status.toUpperCase()}
      </>
    ),
  },
};

const getMenuItems = (user: User): TableMenuItem[] => {
  const userIsInvited = user.status === CompanyContactStatus.Invited;
  const userIsActive = user.status === CompanyContactStatus.Active;
  const userIsDisabled = user.status === CompanyContactStatus.Disabled;

  return [
    { label: 'Update', value: 'onUpdateClick' },
    ...(userIsInvited
      ? [
          { label: 'Resend Invite', value: 'onResendInviteClick' },
          { label: 'Delete', value: 'onDeleteClick' },
        ]
      : []),
    ...(userIsActive ? [{ label: 'Disable', value: 'onDisableClick' }] : []),
    ...(userIsDisabled
      ? [
          { label: 'Enable', value: 'onEnableClick' },
          { label: 'Delete', value: 'onDeleteClick' },
        ]
      : []),
  ];
};

export const Users = () => {
  const { data: company } = useApiQuery(getCompany);
  const [showModal, setShowModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');
  const companyId = company?.uuid ?? '';

  const { data: companyUsers, isLoading: loading } = useApiQuery(
    getCompanyUsers,
    {
      params: { companyId },
      enabled: companyId !== '',
    }
  );
  const { mutate: resendUserInvite } = useApiMutation(resendInvite);
  const { mutate: deleteUser } = useApiMutation(deleteCompanyUser);
  const { mutate: disableUser } = useApiMutation(updateCompanyUserStatus);
  const { mutate: enableUser } = useApiMutation(updateCompanyUserStatus);
  if (loading) {
    return <Spinner fullScreen />;
  }

  const openModal = (userId?: string) => {
    if (userId) {
      setCurrentUserId(userId);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setCurrentUserId('');
    setShowModal(false);
  };

  const selectedUser = companyUsers?.find(
    (employee) => employee.id === currentUserId
  ) as UserIdentity;

  return (
    <>
      <InfoSection
        title="Manage Team"
        primaryAction={{
          text: 'Invite',
          onClick: openModal,
        }}
        testId="manage-team"
      />
      {companyUsers?.length === 0 ? (
        <Empty message="There are no users registered at this time." />
      ) : (
        <Table
          itemKey="id"
          data={companyUsers || []}
          dontSortByDefault
          cellConfig={TABLE_CELLS}
          secondary
          onMenuItemClick={(
            option:
              | 'onUpdateClick'
              | 'onDeleteClick'
              | 'onResendInviteClick'
              | 'onDisableClick'
              | 'onEnableClick',
            user: UserIdentity
          ) => {
            const options = {
              onUpdateClick: () => openModal(user.id),
              onResendInviteClick: () =>
                resendUserInvite({
                  email: user.email,
                  isInvite: true,
                }),
              onDeleteClick: () => deleteUser(user),
              onDisableClick: () =>
                disableUser({
                  id: user.id,
                  status: CompanyContactStatus.Disabled,
                }),
              onEnableClick: () =>
                enableUser({
                  id: user.id,
                  status: CompanyContactStatus.Active,
                }),
            };

            options[option]();
          }}
          getMenuItems={getMenuItems}
        />
      )}
      <AddUser
        isVisible={showModal}
        closeModal={closeModal}
        companyId={companyId}
        companyType={company?.type[0] || ''}
        currentUser={selectedUser}
      />
    </>
  );
};
